/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { matchSelectableGuide, useCurrentQueueItem } from "@packages/media";
import type { ThemeType, WithButtonWrapper } from "@packages/ui";
import {
  AudioControlsIcon,
  colors,
  constantSemanticColors,
  IconButton,
  ModalDropdownMenu,
  ModalDropdownMenuHeader,
  ModalTabContainer,
  ModalTabContent,
  ThemeProvider,
  useIsDesktopViewport,
  zIndices,
} from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import { useContext, useEffect, useState } from "react";

import { PlayerInternalContext } from "../../core/MediaPlayer.emotion";
import { FragmentAsChild } from "../../primitives/FragmentAsChild";
import { SelectBackgroundTab } from "./SelectBackground/SelectBackgroundTab.emotion";
import { SelectBackgroundTabContent } from "./SelectBackground/SelectBackgroundTabContent.emotion";
import { SelectGuideTab } from "./SelectGuide/SelectGuideTab.emotion";
import { SelectGuideTabContent } from "./SelectGuide/SelectGuideTabContent.emotion";
import { SelectLengthTab } from "./SelectLength/SelectLengthTab.emotion";
import { SelectLengthTabContent } from "./SelectLength/SelectLengthTabContent.emotion";
import { SelectPlaybackSpeedTab } from "./SelectPlaybackSpeed/SelectPlaybackSpeedTab.emotion";
import { SelectPlaybackSpeedTabContent } from "./SelectPlaybackSpeed/SelectPlaybackSpeedTabContent.emotion";
import { SelectTimerTab } from "./SelectTimer/SelectTimerTab.emotion";
import { SelectTimerTabContent } from "./SelectTimer/SelectTimerTabContent.emotion";

const darkModeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});
const containerStyle: Interpolation = {
  background: colors.transparent,
};

const defaultOverlayStyle: Interpolation = {
  zIndex: zIndices.overlayOverMediaPlayer,
};

const defaultZAxisStyle: Interpolation = {
  zIndex: zIndices.modalOverMediaPlayer,
};

export type MediaMenuProps = WithButtonWrapper<{ showControls?: boolean }>;

export type MediaMenuTabProps = { disabled?: boolean };

export const MediaMenu = ({
  Wrapper = FragmentAsChild,
  showControls = true,
}: MediaMenuProps) => {
  const t = useTranslations();
  const { currentItem } = useCurrentQueueItem();
  const analytics = useAnalytics();
  const { popupRef } = useContext(PlayerInternalContext);
  const [open, setOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktopViewport();

  useEffect(() => {
    if (!showControls && isDesktop && open) {
      setOpen(false);
    }
  }, [showControls, isDesktop, open]);

  if (!currentItem)
    return (
      <IconButton
        key={"mediaSettingsButton"}
        icon={
          <AudioControlsIcon fill={constantSemanticColors.onColorVeryHigh} />
        }
        disabled={true}
        variant="transparentWhite"
        data-testid={HallowDataTestId.MediaSettingsTrigger}
      />
    );

  const { bg_sounds_enabled, speed_changes_enabled, guide_ids } =
    currentItem?.selected_audio ?? {};
  const isRadio = currentItem?.type === "radio_song";
  const hasGuides =
    currentItem?.type === "prayer" &&
    currentItem.prayer.selectable_guides.length > 1;
  let hasLengths = false;
  if (currentItem?.type === "prayer") {
    const selectedGuides = currentItem.prayer.selectable_guides.find((sg) =>
      matchSelectableGuide(sg.guide_ids, guide_ids),
    );
    hasLengths = selectedGuides.tracks.length > 1;
  }

  const contentCSS = [darkModeStyle, defaultZAxisStyle];

  return (
    <ThemeProvider forceTheme="dark" css={containerStyle} as={null}>
      <ModalDropdownMenu
        trigger={
          <Wrapper asChild>
            <IconButton
              key={"mediaSettingsButton"}
              icon={
                <AudioControlsIcon
                  fill={constantSemanticColors.onColorVeryHigh}
                />
              }
              variant="transparentWhite"
              data-testid={HallowDataTestId.MediaSettingsTrigger}
              onClickCapture={() =>
                analytics.track({
                  event: HallowAnalyticsEvent.TappedTrackSettings,
                })
              }
              tabIndex={0}
            />
          </Wrapper>
        }
        dropdownMenuProps={{
          contentCSS: contentCSS,
          portalProps: { container: popupRef.current },
        }}
        modalProps={{
          contentCSS: contentCSS,
          overlayCSS: defaultOverlayStyle,
          portalProps: { container: popupRef.current },
        }}
        open={open}
        onOpenChange={setOpen}
      >
        <ModalTabContainer defaultValue={"menu"}>
          <ModalTabContent
            value={"menu"}
            header={
              <ModalDropdownMenuHeader>
                {t("settings_title")}
              </ModalDropdownMenuHeader>
            }
          >
            <SelectBackgroundTab disabled={!bg_sounds_enabled || isRadio} />
            <SelectGuideTab disabled={!hasGuides || isRadio} />
            <SelectLengthTab disabled={!hasLengths || isRadio} />
            <SelectPlaybackSpeedTab
              disabled={!speed_changes_enabled || isRadio}
            />
            <SelectTimerTab />
          </ModalTabContent>
          <SelectBackgroundTabContent />
          <SelectGuideTabContent />
          <SelectLengthTabContent />
          <SelectPlaybackSpeedTabContent />
          <SelectTimerTabContent />
        </ModalTabContainer>
      </ModalDropdownMenu>
    </ThemeProvider>
  );
};
